import { FC, useEffect, useState } from 'react';

import { Check, Copy, MapPin, Truck } from 'lucide-react';

import { TrackingLinkButton } from './TrackingLinkButton';
import { Button } from '../../../components/ui/Button';
import { OrdersService } from '../../../services/OrdersService';
import { Order } from '../../../types';

const copyTextToClipboard = async (value: string) => {
    if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
    ) {
        await navigator.clipboard.writeText(value);
        return true;
    }

    if (typeof document === 'undefined') {
        return false;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);

    return copied;
};

const CopyValueButton: FC<{ value: string; label: string }> = ({
    value,
    label,
}) => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;

        const timerId = window.setTimeout(() => {
            setCopied(false);
        }, 1500);

        return () => window.clearTimeout(timerId);
    }, [copied]);

    const handleCopy = async () => {
        const success = await copyTextToClipboard(value);

        if (success) {
            setCopied(true);
        }
    };

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={
                copied ? `Скопійовано: ${label}` : `Скопіювати: ${label}`
            }
            title={copied ? 'Скопійовано' : 'Скопіювати'}
            onClick={handleCopy}
        >
            {copied ? (
                <Check className="h-3.5 w-3.5" />
            ) : (
                <Copy className="h-3.5 w-3.5" />
            )}
        </Button>
    );
};

const DeliveryDetailRow: FC<{
    label: string;
    value: string;
    copyValue?: string;
    showCopyAction?: boolean;
}> = ({ label, value, copyValue, showCopyAction = false }) => (
    <div className="flex flex-wrap items-center gap-1">
        <span>
            {label}: {value}
        </span>
        {showCopyAction && copyValue && (
            <CopyValueButton value={copyValue} label={label} />
        )}
    </div>
);

export const PurchasesDeliveryDetails: FC<{
    order: Order;
    showCopyActions?: boolean;
}> = ({ order, showCopyActions = false }) => {
    const delivery = order.delivery;
    if (!delivery) return null;

    const deliveryPointLabel =
        delivery.delivery_point_type === 'parcel_locker'
            ? 'Поштомат'
            : 'Відділення';
    const deliveryPointValue = delivery.delivery_point_number
        ? `№${delivery.delivery_point_number}`
        : '';

    return (
        <div className="mt-2 mb-3 rounded-xl border bg-muted/20 p-3">
            <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                {delivery.method === 'pickup' ? (
                    <MapPin className="h-4 w-4" />
                ) : (
                    <Truck className="h-4 w-4" />
                )}
                {OrdersService.getDeliveryMethodLabel(delivery.method)}
            </div>
            <div className="space-y-0.5 text-xs text-muted-foreground">
                {delivery.recipient_name && (
                    <DeliveryDetailRow
                        label="ПІБ покупця"
                        value={delivery.recipient_name}
                        copyValue={delivery.recipient_name}
                        showCopyAction={showCopyActions}
                    />
                )}
                {delivery.recipient_phone && (
                    <DeliveryDetailRow
                        label="Телефон покупця"
                        value={delivery.recipient_phone}
                        copyValue={delivery.recipient_phone}
                        showCopyAction={showCopyActions}
                    />
                )}
                {delivery.city && (
                    <DeliveryDetailRow
                        label="Місто"
                        value={delivery.city}
                        copyValue={delivery.city}
                        showCopyAction={showCopyActions}
                    />
                )}
                {delivery.delivery_point_number && (
                    <DeliveryDetailRow
                        label={deliveryPointLabel}
                        value={deliveryPointValue}
                        copyValue={delivery.delivery_point_number}
                        showCopyAction={showCopyActions}
                    />
                )}
                {delivery.post_office && (
                    <DeliveryDetailRow
                        label="Відділення"
                        value={delivery.post_office}
                        copyValue={delivery.post_office}
                        showCopyAction={showCopyActions}
                    />
                )}
                {delivery.comment_for_seller && (
                    <DeliveryDetailRow
                        label="Коментар"
                        value={delivery.comment_for_seller}
                    />
                )}
            </div>

            {delivery.tracking_number && (
                <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm font-medium">
                        ТТН: {delivery.tracking_number}
                    </span>
                    {delivery.tracking_url && (
                        <TrackingLinkButton href={delivery.tracking_url} />
                    )}
                </div>
            )}
        </div>
    );
};
